import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DirectusTranslatedItem, DirectusTranslatedItemGroup} from '@frontend/directus';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {TranslocoPipe} from '@jsverse/transloco';
import {OverlayListenerOptions, OverlayOptions} from 'primeng/api';
import {Checkbox} from 'primeng/checkbox';
import {InputTextModule} from 'primeng/inputtext';
import {MultiSelect} from 'primeng/multiselect';
import {Tag} from 'primeng/tag';
import {DirectusTooltipComponent} from '../../../directus-tooltip/directus-tooltip.component';
import {ModelFieldSelectComponent} from '../../../inputs/model-field/model-field-select/model-field-select.component';
import {ModuleFunctionLabelComponent} from '../../../module-function-label/module-function-label.component';
import {FunctionalScopeEntry} from '../project-references.types';
import {FunctionalScopeChip, ModuleSelectionState} from './functional-scope-picker.types';

@Component({
  selector: 'app-functional-scope-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Checkbox,
    DirectusTooltipComponent,
    FormsModule,
    InputTextModule,
    ModelFieldSelectComponent,
    ModuleFunctionLabelComponent,
    MultiSelect,
    Tag,
    TranslateDirectusPipe,
    TranslocoPipe,
  ],
  templateUrl: './functional-scope-picker.component.html',
})
export class FunctionalScopePickerComponent {
  protected readonly ModuleSelectionState = ModuleSelectionState;

  readonly position = input.required<number>();
  readonly moduleGroups = input.required<DirectusTranslatedItemGroup[]>();
  readonly statusOptions = input.required<DirectusTranslatedItem[]>();
  readonly readOnly = input<boolean>(false);

  readonly value = model<FunctionalScopeEntry[]>([]);

  private readonly functionsSelect = viewChild.required(MultiSelect);

  protected readonly functionsPanelStyle = signal<Record<string, string>>({});

  protected readonly isEditable = computed<boolean>(() => !this.readOnly());

  protected readonly selectedCount = computed<number>(() => this.value().length);

  protected readonly functionsOverlayOptions: OverlayOptions = {
    listener: (event: Event, options?: OverlayListenerOptions): boolean =>
      options?.type === 'outside' && this.isStatusOverlayClick(event)
        ? false
        : (options?.valid ?? true),
  };

  protected readonly selectedFunctionIds = computed<string[]>(() =>
    this.value().map((entry) => entry.functionId),
  );

  protected readonly statusByFunctionId = computed<Map<string, string>>(
    () => new Map(this.value().map((entry) => [entry.functionId, entry.statusId])),
  );

  protected readonly moduleSelectionStates = computed<Map<string, ModuleSelectionState>>(() => {
    const statusByFunctionId = this.statusByFunctionId();

    return new Map(
      this.moduleGroups().map((moduleGroup) => {
        const selectedCount = moduleGroup.items.filter((item) =>
          statusByFunctionId.has(item.id),
        ).length;

        return [
          moduleGroup.id,
          this.toModuleSelectionState(selectedCount, moduleGroup.items.length),
        ];
      }),
    );
  });

  protected readonly chips = computed<FunctionalScopeChip[]>(() => {
    const statusByFunctionId = this.statusByFunctionId();
    const statusesById = new Map(this.statusOptions().map((status) => [status.id, status]));

    return this.moduleGroups()
      .flatMap((moduleGroup) => moduleGroup.items)
      .flatMap((item) => {
        const statusId = statusByFunctionId.get(item.id);

        return statusId === undefined
          ? []
          : [
              {
                functionId: item.id,
                functionTranslations: item.translations,
                statusTranslations: statusesById.get(statusId)?.translations ?? [],
              },
            ];
      });
  });

  protected onSelectionChange(functionIds: string[]): void {
    const statusByFunctionId = this.statusByFunctionId();
    const defaultStatusId = this.statusOptions()[0]?.id;

    if (defaultStatusId === undefined) {
      return;
    }

    this.value.set(
      functionIds.map((functionId) => ({
        functionId: functionId,
        statusId: statusByFunctionId.get(functionId) ?? defaultStatusId,
      })),
    );
  }

  protected onFunctionsPanelShow(): void {
    const selectWidth: number = this.functionsSelect().el.nativeElement.offsetWidth;

    this.functionsPanelStyle.set({width: `${selectWidth * 1.5}px`});
  }

  protected onModuleToggle(moduleGroup: DirectusTranslatedItemGroup, checked: boolean): void {
    const moduleFunctionIds = moduleGroup.items.map((item) => item.id);
    const otherFunctionIds = this.selectedFunctionIds().filter(
      (functionId) => !moduleFunctionIds.includes(functionId),
    );

    this.onSelectionChange(
      checked ? [...otherFunctionIds, ...moduleFunctionIds] : otherFunctionIds,
    );
  }

  protected onStatusChange(functionId: string, statusId: string): void {
    this.value.update((entries) =>
      entries.map((entry) =>
        entry.functionId === functionId ? {functionId: functionId, statusId: statusId} : entry,
      ),
    );
  }

  protected removeFunction(functionId: string): void {
    this.value.update((entries) => entries.filter((entry) => entry.functionId !== functionId));
  }

  private isStatusOverlayClick(event: Event): boolean {
    return event.target instanceof Element && event.target.closest('.p-select-overlay') !== null;
  }

  private toModuleSelectionState(selectedCount: number, totalCount: number): ModuleSelectionState {
    if (selectedCount === 0) {
      return ModuleSelectionState.NONE;
    }

    return selectedCount === totalCount ? ModuleSelectionState.ALL : ModuleSelectionState.SOME;
  }
}
